"use client";

import { Component, Suspense, lazy, useMemo } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useTranslations } from "next-intl";
import ToolHeroSection from "./ToolHeroSection";
import PricingCard from "@/components/home/PricingCard";
import { SITE_URL } from "@/config/i18n";
import HeroWidgetSkeleton from "@/components/layout/HeroWidgetSkeleton";

const FAQSection = lazy(() => import("@/components/home/FAQSection"));
const Footer = lazy(() => import("@/components/layout/Footer.jsx"));
const TestimonialSection = lazy(() => import("@/components/home/TestimonialSection"));
const MeetLunaSection = lazy(() => import("@/components/home/MeetLunaSection"));
const WhyItWorksSection = lazy(() => import("@/components/home/WhyItWorksSection"));

// Error Fallback Component
function ToolErrorFallback({ error, onReset }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050316]">
      <div className="text-center p-8">
        <h2 className="text-2xl font-marcellus text-white mb-4">
          Something went wrong
        </h2>
        <p className="text-gray-400 mb-6">{error?.message || "An unexpected error occurred"}</p>
        <button
          onClick={onReset}
          className="px-6 py-3 bg-[#A45DE8] text-white rounded-lg hover:bg-[#6A3683] transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

// Error Boundary Class Component
class ToolErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[OracleLandingPageLayout] Error caught by boundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ToolErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

export default function OracleLandingPageLayout({
  toolConfig,
  initialUsage,
  initialTier,
  heroPrefetchedAt,
  locale,
  canonicalPath,
}) {
  // Get translations - hooks must be called unconditionally
  const t = useTranslations(toolConfig.translationNamespace);
  const tOracle = useTranslations("oracle.pendulum");
  
  // Safely get translation values - handle missing keys gracefully
  let hero = {};
  let featured = {};
  let howItWorks = {};
  let pricing = {};
  let testimonials = {};
  let faq = {};
  let footer = {};
  let meetLuna = {};
  let whyItWorks = {};
  let metadata = toolConfig.defaultMetadata;
  
  try {
    const heroRaw = t.raw("hero");
    if (heroRaw && typeof heroRaw === 'object' && !Array.isArray(heroRaw)) {
      hero = heroRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const featuredRaw = t.raw("featured");
    if (featuredRaw && typeof featuredRaw === 'object' && !Array.isArray(featuredRaw)) {
      featured = featuredRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const howItWorksRaw = t.raw("howItWorks");
    if (howItWorksRaw && typeof howItWorksRaw === 'object' && !Array.isArray(howItWorksRaw)) {
      howItWorks = howItWorksRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const pricingRaw = t.raw("pricing");
    if (pricingRaw && typeof pricingRaw === 'object' && !Array.isArray(pricingRaw)) {
      pricing = pricingRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const testimonialsRaw = t.raw("testimonials");
    if (testimonialsRaw && typeof testimonialsRaw === 'object' && !Array.isArray(testimonialsRaw)) {
      testimonials = testimonialsRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const faqRaw = t.raw("faq");
    if (faqRaw && typeof faqRaw === 'object' && !Array.isArray(faqRaw)) {
      faq = faqRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const footerRaw = t.raw("footer");
    if (footerRaw && typeof footerRaw === 'object' && !Array.isArray(footerRaw)) {
      footer = footerRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const meetLunaRaw = t.raw("meetLuna");
    if (meetLunaRaw && typeof meetLunaRaw === 'object' && !Array.isArray(meetLunaRaw)) {
      meetLuna = meetLunaRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  try {
    const whyItWorksRaw = t.raw("whyItWorks");
    if (whyItWorksRaw && typeof whyItWorksRaw === 'object' && !Array.isArray(whyItWorksRaw)) {
      whyItWorks = whyItWorksRaw;
    }
  } catch (error) {
    // Key doesn't exist, use empty object
  }
  
  
  try {
    const metadataRaw = t.raw("metadata");
    if (metadataRaw && typeof metadataRaw === 'object' && !Array.isArray(metadataRaw)) {
      metadata = { ...toolConfig.defaultMetadata, ...metadataRaw };
    }
  } catch (error) {
    // Key doesn't exist, use default metadata
  }
  
  // Add widget titles and CTA to hero content for custom tools (pendulum)
  if (toolConfig.translationNamespace === "pendulumYesNo") {
    try {
      hero.widgetTitle = {
        free: tOracle("widgetTitle.free", { defaultValue: "Get a FREE Pendulum Reading by the Oracle" }),
        premium: tOracle("widgetTitle.premium", { defaultValue: "Unlimited Pendulum Readings" }),
      };
      // Add pendulum-specific CTA text
      hero.cta = {
        primary: tOracle("cta.primary", { defaultValue: "Consult the Pendulum" }),
        primaryLoading: tOracle("cta.primaryLoading", { defaultValue: "Pendulum is swinging..." }),
      };
    } catch (error) {
      // Fallback if translation fails
      hero.widgetTitle = {
        free: "Get a FREE Pendulum Reading by the Oracle",
        premium: "Unlimited Pendulum Readings",
      };
      hero.cta = {
        primary: "Consult the Pendulum",
        primaryLoading: "Pendulum is swinging...",
      };
    }
  }

  // Add widget titles and CTA to hero content for dream interpreter
  if (toolConfig.translationNamespace === "dreamInterpreter") {
    try {
      hero.widgetTitle = {
        free: t("widgetTitle.free", { defaultValue: "Get a FREE Dream Interpretation by the Oracle" }),
        premium: t("widgetTitle.premium", { defaultValue: "Unlimited Dream Interpretations" }),
      };
      // Add dream interpreter-specific CTA text
      hero.cta = {
        primary: t("cta.primary", { defaultValue: "Interpret Dream" }),
        primaryLoading: t("cta.primaryLoading", { defaultValue: "Interpreting your dream..." }),
      };
    } catch (error) {
      // Fallback if translation fails
      hero.widgetTitle = {
        free: "Get a FREE Dream Interpretation by the Oracle",
        premium: "Unlimited Dream Interpretations",
      };
      hero.cta = {
        primary: "Interpret Dream",
        primaryLoading: "Interpreting your dream...",
      };
    }
  }

  // Add widget titles and CTA to hero content for daily card
  if (toolConfig.translationNamespace === "dailyCard") {
    try {
      hero.widgetTitle = {
        free: t("widgetTitle.free", { defaultValue: "Get a FREE Daily Oracle Card Reading" }),
        premium: t("widgetTitle.premium", { defaultValue: "Unlimited Daily Oracle Card Readings" }),
      };
      // Add daily card-specific CTA text
      hero.cta = {
        primary: t("cta.primary", { defaultValue: "Draw Your Cards" }),
        primaryLoading: t("cta.primaryLoading", { defaultValue: "Drawing your cards..." }),
      };
    } catch (error) {
      // Fallback if translation fails
      hero.widgetTitle = {
        free: "Get a FREE Daily Oracle Card Reading",
        premium: "Unlimited Daily Oracle Card Readings",
      };
      hero.cta = {
        primary: "Draw Your Cards",
        primaryLoading: "Drawing your cards...",
      };
    }
  }

  // Dynamically import tool component and loading skeleton (client-side only)
  // Note: We need to use a function that returns the import, not a variable path
  const ToolComponent = useMemo(() => {
    if (!toolConfig.toolComponentPath) {
      return null;
    }
    
    // Create a dynamic import function based on the path
    const importPath = toolConfig.toolComponentPath;
    const importSkeletonPath = toolConfig.loadingSkeletonPath;
    
    // Create a safe import function that handles missing components
    // Returns a valid React component (even if it renders nothing) to avoid React lazy() errors
    const PlaceholderComponent = () => null;
    
    const safeImport = async () => {
      try {
      if (importPath === "@/components/oracle/PendulumOracle") {
          return await import("@/components/oracle/PendulumOracle");
      }
        if (importPath === "@/components/oracle/DreamInterpreter") {
          return await import("@/components/oracle/DreamInterpreter");
        }
        if (importPath === "@/components/oracle/DailyCardWidget") {
          return await import("@/components/oracle/DailyCardWidget");
        }
        // If no match, return a placeholder that won't cause React errors
        return { default: PlaceholderComponent };
      } catch (error) {
        // If import fails, return a placeholder component
        console.warn(`[OracleLandingPageLayout] Failed to load component: ${importPath}`, error);
        return { default: PlaceholderComponent };
      }
    };
    
    return dynamic(safeImport, {
      ssr: false,
      loading: () => {
        // Use tool-specific skeleton if available
        if (importSkeletonPath === "@/components/oracle/skeletons/PendulumSkeleton") {
          const Skeleton = dynamic(() => import("@/components/oracle/skeletons/PendulumSkeleton"), {
            ssr: false,
          });
          return <Skeleton />;
        }
        if (importSkeletonPath === "@/components/oracle/skeletons/DreamInterpreterSkeleton") {
          const Skeleton = dynamic(() => import("@/components/oracle/skeletons/DreamInterpreterSkeleton"), {
            ssr: false,
          });
          return <Skeleton />;
        }
        if (importSkeletonPath === "@/components/oracle/skeletons/DailyCardSkeleton") {
          const Skeleton = dynamic(() => import("@/components/oracle/skeletons/DailyCardSkeleton"), {
            ssr: false,
          });
          return <Skeleton />;
        }
        return <HeroWidgetSkeleton />;
      },
    });
  }, [toolConfig.toolComponentPath, toolConfig.loadingSkeletonPath]);

  const LoadingSkeleton = useMemo(() => {
    if (!toolConfig.loadingSkeletonPath) {
      return HeroWidgetSkeleton;
    }
    
    // Map string paths to actual imports
    if (toolConfig.loadingSkeletonPath === "@/components/oracle/skeletons/PendulumSkeleton") {
      return dynamic(() => import("@/components/oracle/skeletons/PendulumSkeleton"), {
        ssr: false,
      });
    }
    if (toolConfig.loadingSkeletonPath === "@/components/oracle/skeletons/DreamInterpreterSkeleton") {
      return dynamic(() => import("@/components/oracle/skeletons/DreamInterpreterSkeleton"), {
        ssr: false,
      });
    }
    if (toolConfig.loadingSkeletonPath === "@/components/oracle/skeletons/DailyCardSkeleton") {
      return dynamic(() => import("@/components/oracle/skeletons/DailyCardSkeleton"), {
        ssr: false,
      });
    }
    return HeroWidgetSkeleton;
  }, [toolConfig.loadingSkeletonPath]);

  return (
    <ToolErrorBoundary>
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": toolConfig.schemaType || "WebPage",
            name: toolConfig.schemaName || toolConfig.toolName,
            url: `${SITE_URL}/${locale}${canonicalPath}`,
            description: metadata.schemaDescription || metadata.description,
          }),
        }}
      />
      
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#050316" }}>
        <style>{`
          @keyframes mystical-spin {
            0% { transform: rotate(0deg); }
            20% { transform: rotate(180deg); }
            40% { transform: rotate(360deg); }
            50% { transform: rotate(360deg); }
            60% { transform: rotate(180deg); }
            80% { transform: rotate(0deg); }
            100% { transform: rotate(0deg); }
          }
        `}</style>
        
        {/* Hero Section with Tool */}
        <section id="herosection">
          <Suspense fallback={<LoadingSkeleton />}>
            <ToolHeroSection
              toolConfig={toolConfig}
              customWidget={ToolComponent || undefined}
              content={hero}
              initialUsage={initialUsage}
              initialTier={initialTier}
              prefetchedAt={heroPrefetchedAt}
            />
          </Suspense>
        </section>

        {/* Meet Luna Section */}
        {meetLuna && Object.keys(meetLuna).length > 0 && meetLuna.title && (
          <Suspense fallback={<div className="text-center text-gray-400 py-16">Loading...</div>}>
            <MeetLunaSection content={meetLuna} />
          </Suspense>
        )}

        {/* Why It Works Section */}
        {whyItWorks && Object.keys(whyItWorks).length > 0 && whyItWorks.title && (
          <Suspense fallback={<div className="text-center text-gray-400 py-16">Loading...</div>}>
            <WhyItWorksSection content={whyItWorks} testimonials={testimonials?.items ?? []} />
          </Suspense>
        )}

        {/* Featured On */}
        {featured?.title && (
          <section id="featured-on" className="py-12 md:py-16 text-white">
            <div className="container mx-auto px-4 md:px-6 text-center">
              <span
                className="inline-block text-purple-400 text-sm font-semibold tracking-wider uppercase mb-2"
                style={{ fontFamily: "var(--font-marcellus)" }}
              >
                {featured.title}
              </span>
              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 mt-8">
                <a
                  href="https://theresanaiforthat.com/ai/ask-the-oracle/?ref=featured&v=6060314"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="transform transition-transform hover:scale-105"
                >
                  <Image
                    src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
                    alt="Featured on TheresAnAIForThat.com"
                    width={200}
                    height={50}
                    className="object-contain"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://tinylaun.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transform transition-transform hover:scale-105"
                >
                  <Image
                    src="https://tinylaun.ch/tinylaunch_badge_featured_on.svg"
                    alt="TinyLaunch Badge"
                    width={202}
                    height={75}
                    className="object-contain"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://www.producthunt.com/posts/ask-the-oracle?utm_source=badge-featured&utm_medium=badge&utm_source=badge-ask-the-oracle"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transform transition-transform hover:scale-105"
                >
                  <Image
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=968103&theme=neutral&t=1748257432718"
                    alt="Ask The Oracle - Where ancient wisdom meets modern AI | Product Hunt"
                    width={250}
                    height={54}
                    className="object-contain"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq?.title && (
          <Suspense fallback={<div className="text-center text-gray-400">{faq.loading}</div>}>
            <FAQSection
              faqs={faq.items || []}
              tagline={faq.tagline}
              title={faq.title}
              description={faq.description}
              emptyMessage={faq.empty}
            />
          </Suspense>
        )}

        {/* Footer */}
        <Suspense fallback={<div className="text-center text-gray-400">{footer.loading}</div>}>
          <Footer />
        </Suspense>
      </div>
    </ToolErrorBoundary>
  );
}

